import { FundingRequestsParams } from '../../api/fundingApi'

/**
 * Filter + sort model for the funding requests table and the translation
 * from UI selections to the query params the `funding/requests` endpoint
 * understands. The panel keeps a draft `FundingFilterState`; on Apply it is
 * converted to params here and handed up to the dashboard, which re-fetches.
 */

export interface FundingFilterState {
  /** relative preset key: '24h' | '3d' | '7d' | 'all' */
  createdWithin: string
  updatedWithin: string
  /** relative preset key: '3d' | '7d' | '30d' | 'all' */
  expiryWithin: string
  /** funding program labels (substring OR-match on program_name) */
  programNames: string[]
  /** API status enum values */
  statuses: string[]
  amount: [number, number]
}

export const AMOUNT_MIN = 0
export const AMOUNT_MAX = 100000
export const AMOUNT_STEP = 1000

export const emptyFilterState = (): FundingFilterState => ({
  createdWithin: 'all',
  updatedWithin: 'all',
  expiryWithin: 'all',
  programNames: [],
  statuses: [],
  amount: [AMOUNT_MIN, AMOUNT_MAX],
})

export interface FilterOption {
  key: string
  label: string
}

export const CREATED_OPTIONS: FilterOption[] = [
  { key: '24h', label: 'In 24 hours' },
  { key: '3d', label: 'In 3 days' },
  { key: '7d', label: 'In 7 days' },
  { key: 'all', label: 'All time' },
]

export const UPDATED_OPTIONS: FilterOption[] = CREATED_OPTIONS

export const EXPIRY_OPTIONS: FilterOption[] = [
  { key: '3d', label: 'Within 3 days' },
  { key: '7d', label: 'Within 7 days' },
  { key: '30d', label: 'Within 30 days' },
  { key: 'all', label: 'All time' },
]

export const PROGRAM_OPTIONS: FilterOption[] = [
  { key: 'POC', label: 'POC' },
  { key: 'MPOPP', label: 'MPOPP' },
]

export const STATUS_OPTIONS: FilterOption[] = [
  { key: 'DRAFT', label: 'Draft' },
  { key: 'ACTION_REQUIRED', label: 'Action required' },
  { key: 'SUBMITTED', label: 'Submitted' },
  { key: 'APPROVED', label: 'Approved' },
  { key: 'PAID', label: 'Paid' },
  { key: 'REJECTED', label: 'Rejected' },
]

const DAY_MS = 24 * 60 * 60 * 1000
const PRESET_DAYS: Record<string, number> = {
  '24h': 1,
  '3d': 3,
  '7d': 7,
  '30d': 30,
}

const isoDaysFromNow = (days: number) =>
  new Date(Date.now() + days * DAY_MS).toISOString()

const labelFor = (options: FilterOption[], key: string) =>
  options.find(option => option.key === key)?.label ?? key

export const filterStateToParams = (
  state: FundingFilterState
): Partial<FundingRequestsParams> => {
  const params: Partial<FundingRequestsParams> = {}
  if (state.createdWithin !== 'all')
    params.created_after = isoDaysFromNow(-PRESET_DAYS[state.createdWithin])
  if (state.updatedWithin !== 'all')
    params.updated_after = isoDaysFromNow(-PRESET_DAYS[state.updatedWithin])
  if (state.expiryWithin !== 'all')
    params.expiry_before = isoDaysFromNow(PRESET_DAYS[state.expiryWithin])
  if (state.programNames.length) params.program_name = state.programNames
  if (state.statuses.length) params.status = state.statuses
  if (state.amount[0] > AMOUNT_MIN) params.amount_min = String(state.amount[0])
  if (state.amount[1] < AMOUNT_MAX) params.amount_max = String(state.amount[1])
  return params
}

export const isAmountFiltered = (amount: [number, number]) =>
  amount[0] > AMOUNT_MIN || amount[1] < AMOUNT_MAX

const formatMoney = (value: number) => `$${value.toLocaleString('en-US')}`

const PROGRAM_CHIP_PREFIX = 'program:'
const STATUS_CHIP_PREFIX = 'status:'

/** A chip in the "Filters applied" bar: a stable key + a label/value pair. */
export interface AppliedChip {
  key: string
  label: string
  value: string
}

export const appliedFilterChips = (
  state: FundingFilterState
): AppliedChip[] => {
  const chips: AppliedChip[] = []
  if (state.createdWithin !== 'all')
    chips.push({
      key: 'created',
      label: 'Created at',
      value: labelFor(CREATED_OPTIONS, state.createdWithin),
    })
  if (state.updatedWithin !== 'all')
    chips.push({
      key: 'updated',
      label: 'Last updated',
      value: labelFor(UPDATED_OPTIONS, state.updatedWithin),
    })
  if (state.expiryWithin !== 'all')
    chips.push({
      key: 'expiry',
      label: 'Expiry date',
      value: labelFor(EXPIRY_OPTIONS, state.expiryWithin),
    })
  state.programNames.forEach(name =>
    chips.push({
      key: `${PROGRAM_CHIP_PREFIX}${name}`,
      label: 'Program',
      value: name,
    })
  )
  state.statuses.forEach(key =>
    chips.push({
      key: `${STATUS_CHIP_PREFIX}${key}`,
      label: 'Status',
      value: labelFor(STATUS_OPTIONS, key),
    })
  )
  if (isAmountFiltered(state.amount))
    chips.push({
      key: 'amount',
      label: 'Amount',
      value: `${formatMoney(state.amount[0])} - ${formatMoney(state.amount[1])}`,
    })
  return chips
}

/** Clear the single filter a chip represents, addressed by its stable key. */
export const removeChipByKey = (
  state: FundingFilterState,
  key: string
): FundingFilterState => {
  if (key === 'created') return { ...state, createdWithin: 'all' }
  if (key === 'updated') return { ...state, updatedWithin: 'all' }
  if (key === 'expiry') return { ...state, expiryWithin: 'all' }
  if (key === 'amount') return { ...state, amount: [AMOUNT_MIN, AMOUNT_MAX] }
  if (key.startsWith(PROGRAM_CHIP_PREFIX)) {
    const name = key.slice(PROGRAM_CHIP_PREFIX.length)
    return {
      ...state,
      programNames: state.programNames.filter(n => n !== name),
    }
  }
  if (key.startsWith(STATUS_CHIP_PREFIX)) {
    const status = key.slice(STATUS_CHIP_PREFIX.length)
    return { ...state, statuses: state.statuses.filter(s => s !== status) }
  }
  return state
}

/**
 * Sort is a single choice across all sections. Each option's `key` is
 * `<column>_<direction>` (e.g. `company_name_desc`), split into the endpoint's
 * `sort_column` (last_activity | created | updated | amount | expiry |
 * company_name) + `sort` (asc | desc) params at the API layer (see
 * `getFundingRequests`).
 */
export interface SortOption {
  key: string
  label: string
}

export interface SortSection {
  title: string
  options: SortOption[]
}

export const SORT_SECTIONS: SortSection[] = [
  {
    title: 'Company name',
    options: [
      { key: 'company_name_asc', label: 'Ascending order (A to Z)' },
      { key: 'company_name_desc', label: 'Descending order (Z to A)' },
    ],
  },
  {
    title: 'Expiry date',
    options: [
      { key: 'expiry_desc', label: 'Oldest first' },
      { key: 'expiry_asc', label: 'Newest first' },
    ],
  },
  {
    title: 'Amount',
    options: [
      { key: 'amount_desc', label: 'High to Low' },
      { key: 'amount_asc', label: 'Low to High' },
    ],
  },
]

/** The "Sorting applied" chip label/value for a selected `sort` param. */
export const sortChip = (
  sort?: string
): { label: string; value: string } | null => {
  if (!sort) return null
  for (const section of SORT_SECTIONS) {
    const option = section.options.find(o => o.key === sort)
    if (option) return { label: section.title, value: option.label }
  }
  return null
}
